import React, { useState } from "react";
import Select from "react-select";
import { withI18n } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
// @ts-ignore
import { CWBModal } from "cwb-react";
import { toast } from "react-toastify";
import { Button, ButtonGroup } from "../Common";
import { iRootState } from "../../store";
import { IAuditionsDto } from "../../shared/dto/IKioskDto";
import CWBRTheme from "../../Theme/CWBRSTheme";

interface Props extends StateProps, DispatchProps {
  t?: any;
  hideModal: () => void;
  loadSessionAuditions: (scheduleId: number) => void;
  scheduleId: number;
}

interface Actor {
  label: string;
  value: number;
}

const SendAuditionTimeUpdateModal = (props: Props) => {
  const numActorsTotal = props.auditionsDetails.filter(
    (x) => x.isAuditionTimeUpdated
  ).length;
  const [selectedNames, setNames] = useState([]);

  const { t } = props;
  const linkSelectorOptions = [
    { label: `${t("Changes to Schedule")}`, value: 1 },
    { label: `${t("Select individual actors")}`, value: 2 },
  ];
  const [selected, setSelected] = useState(linkSelectorOptions[0]);

  const listOfNames = props.auditionsDetails
    .filter((x) => x.isAuditionTimeUpdated)
    .reduce((arr: any, actorInfo: any) => {
      let checkValue: Actor = {
        label: actorInfo.actor,
        value: actorInfo.clientId,
      };
      let found = arr.some((name: any) => name.label === actorInfo.actor);

      if (!found) arr.push(checkValue);
      return arr;
    }, []);

  const checkWhichSelected = (value: any) => {
    let selectedValue = value.value;
    if (selectedValue !== 2) {
      setNames([]);
    }
    setSelected(linkSelectorOptions[selectedValue - 1]);
  };

  const addToList = (value: any) => {
    setNames(value.map((x: { label: string; value: number }) => x.value));
  };

  const resend = async () => {
    const success = await props.sendAuditionTimeChanges({
      scheduleId: props.scheduleId,
      allActors: false,
      newActors: false,
      targetIds:
        selected.value === 2
          ? selectedNames
          : listOfNames.map((x: Actor) => x.value),
    });
    if (success) {
      toast.success(t("Virtual Audition link resent!"), {
        position: "top-right",
      });
      await props.loadSessionAuditions(props.scheduleId);
    } else if (success === false) {
      toast.error(
        <span>
          {t("Some attendees did not receive the Virtual Audition Link.")}
          &nbsp;
          <StyledButton onClick={() => props.setShowFailedEmailModal(true)}>
            {t("view details")}
          </StyledButton>
        </span>,
        {
          position: "top-right",
          autoClose: false,
        }
      );
    }
    props.hideModal();
  };

  let baseUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL : "";

  return (
    <StyledModal
      className="cwb-modal"
      isOpen={true}
      hideModal={() => props.hideModal()}
      buttons={
        <StyledButtonGroup>
          <Button
            disabled={selectedNames.length === 0 && selected.value === 3}
            onClick={() => resend()}
          >
            <StyledSpan>
              {t("Send")}
              {props.loading && (
                <Spinner src={`${baseUrl}/images/spinner.svg`} />
              )}
            </StyledSpan>
          </Button>
          <Button variant="secondary" onClick={() => props.hideModal()}>
            {t("Cancel")}
          </Button>
        </StyledButtonGroup>
      }
    >
      <Title>{t("Send Audition Time Update")}</Title>
      <Text>
        {t(
          "The Virtual Audition link and all related materials will be sent to the following"
        )}
        :
      </Text>
      <Text>{t("Resend link to")}:</Text>
      <div>
        <Select
          options={linkSelectorOptions}
          placeholder="All actors with audition time changes"
          onChange={checkWhichSelected}
          styles={selectStyle}
          value={selected}
        />
      </div>
      <div>
        {selected.value === 2 ? (
          <div>
            <Text>{t("Recipient(s)")}</Text>
            <Select
              options={listOfNames}
              placeholder="Select one or mulitple"
              onChange={addToList}
              styles={selectStyle}
              isMulti
              autoFocus
            />
          </div>
        ) : (
          <div>
            {t("Actors with updated times on the schedule")}:{" "}
            <span>{numActorsTotal}</span>
          </div>
        )}
      </div>
    </StyledModal>
  );
};

const StyledModal = styled(CWBModal)`
  && {
    width: 600px;
    max-width: unset;
  }

  * {
    text-align: left;
  }
`;

const Title = styled.h1`
  color: #0a1f44;
  font-size: ${(p) => p.theme["xxl-font-size"]};
  font-weight: bold;
`;

const Text = styled.p`
  color: #53627c;
  font-size: 14px;
`;

const selectStyle = {
  control: (styles: any) => ({ ...styles, marginBottom: "1rem" }),
  multiValue: (styles: any) => {
    return {
      ...styles,
      backgroundColor: "#F8F9FB",
      border: "1px solid #C9CED6",
    };
  },
  multiValueRemove: (styles: any) => ({
    ...styles,
    color: "C9CED6",
    hover: {
      backgroundColor: CWBRTheme.red,
    },
  }),
};

const StyledButtonGroup = styled(ButtonGroup)`
  width: 100%;
`;

const StyledSpan = styled.span`
  display: flex;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  & > img {
    margin-left: 4px;
  }
`;

const Spinner = styled.img`
  width: 16px;
`;

const StyledButton = styled.button`
  color: #0f9bf0;
  padding: 0;
  background-color: #fff;
  border: none;
  text-decoration: underline;
`;

interface StateProps {
  loading: boolean;
  showResendLinkModal: { show: boolean; scheduleId: number };
  auditionsDetails: IAuditionsDto[];
}

const mapStateToProps = (state: iRootState) => {
  return {
    loading: state.kiosk.loading,
    auditionsDetails: state.kiosk.sessionAuditions.auditionsDetails,
    showResendLinkModal: state.kiosk.showResendLinkModal,
  };
};

interface DispatchProps {
  setShowFailedEmailModal: (show: boolean) => any;
  sendAuditionTimeChanges: (resendDetails: any) => boolean;
}

const mapDispatchToProps = (dispatch: any) => ({
  setShowFailedEmailModal: dispatch.kiosk.setShowFailedEmailModal,
  sendAuditionTimeChanges: dispatch.auditions.sendAuditionTimeChanges,
});

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(SendAuditionTimeUpdateModal)
);
